import { useCallback, useEffect, useRef, useState } from "react";
import { Message } from "../../app/(protected)/chatbot-ui/page";

export const useScroll = (
  isGenerating: boolean,
  chatMessages: Message[],
  chatId?: string
) => {
  const messagesStartRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isAutoScrolling = useRef(false);
  const prevChatIdRef = useRef<string | undefined>();
  const prevUserMessageCountRef = useRef<number>(0);

  const [isAtTop, setIsAtTop] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [userScrolled, setUserScrolled] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const userMessageCount = chatMessages.reduce((count, message) => {
    return message.role === "user" ? count + 1 : count;
  }, 0);

  const prevIsGeneratingRef = useRef<boolean>(isGenerating);

  useEffect(() => {
    // Only reset userScrolled when generation stops, not when it starts
    const wasGenerating = prevIsGeneratingRef.current;
    const stoppedGenerating = wasGenerating && !isGenerating;

    if (stoppedGenerating) {
      setUserScrolled(false);
    }

    prevIsGeneratingRef.current = isGenerating;
  }, [isGenerating]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const bottom =
      Math.round(target.scrollHeight) - Math.round(target.scrollTop) ===
      Math.round(target.clientHeight);
    setIsAtBottom(bottom);

    const top = target.scrollTop === 0;
    setIsAtTop(top);

    // Only set userScrolled when user intentionally scrolls away from bottom
    // Reset it when they return to bottom to resume auto-scroll
    if (!bottom && !isAutoScrolling.current) {
      setUserScrolled(true);
    } else if (bottom) {
      setUserScrolled(false);
    }

    const isOverflow = target.scrollHeight > target.clientHeight;
    setIsOverflowing(isOverflow);
  }, []);

  const scrollToTop = useCallback(() => {
    const container = messagesStartRef.current?.parentElement as HTMLDivElement | null;
    if (!container) return;

    isAutoScrolling.current = true;
    container.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => {
      isAutoScrolling.current = false;
    }, 100);
  }, []);

  const scrollToBottom = useCallback((instant = false) => {
    const container = messagesEndRef.current?.parentElement as HTMLDivElement | null;
    if (!container) return;

    isAutoScrolling.current = true;
    container.scrollTo({ top: container.scrollHeight, behavior: instant ? "auto" : "smooth" });
    setTimeout(() => {
      isAutoScrolling.current = false;
    }, 100);
  }, []);

  // Move the scroll effect after scrollToBottom is defined
  useEffect(() => {
    const isConversationSwitch = prevChatIdRef.current !== chatId;
    prevChatIdRef.current = chatId;
    const didAddNewUserMessage = userMessageCount > prevUserMessageCountRef.current;
    prevUserMessageCountRef.current = userMessageCount;

    // Use requestAnimationFrame to ensure messages are rendered before scrolling
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Instant scroll when switching conversations, smooth scroll for new messages
        if (isConversationSwitch) {
          scrollToBottom(true);
        } else if (isGenerating && didAddNewUserMessage) {
          // Force a one-time jump to the latest sent message even if user was browsing older messages.
          // This is not sticky: users can still scroll up immediately afterward.
          setUserScrolled(false);
          scrollToBottom(false);
        } else if (isGenerating && !userScrolled) {
          scrollToBottom(false);
        }
      });
    });
  }, [chatId, chatMessages.length, isGenerating, userMessageCount, userScrolled, scrollToBottom]);

  const scrollToMessage = useCallback((messageId: string) => {
    const element = document.getElementById(`message-${messageId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      element.classList.add('highlight-scroll');
      setTimeout(() => {
        element.classList.remove('highlight-scroll');
      }, 1500);
    } else {
      console.warn(`Message element with ID "message-${messageId}" not found.`);
    }
  }, []);

  return {
    messagesStartRef,
    messagesEndRef,
    isAtTop,
    isAtBottom,
    userScrolled,
    isOverflowing,
    handleScroll,
    scrollToTop,
    scrollToBottom,
    scrollToMessage,
  };
};

/*
@keyframes highlight-fade {
  from { background-color: rgba(255, 255, 0, 0.5); }
  to { background-color: transparent; }
}

.highlight-scroll {
  animation: highlight-fade 1.5s ease-out;
}
*/ 
